import { callApi } from "utils/api";
import Swal from 'sweetalert2'
import i18next from 'i18next'
const t = (key) => i18next.t(key)

export default (
  entity,
  messageEntity,
  messagePluralEntity,
  reducer_entity,
) => {
  const reducerEntity = (typeof reducer_entity == 'string') ? reducer_entity : entity

  const getHeaders = (state) => {
    const headers = {};

    // Si estamos en la consola de Super Admin, este es el header que manda.
    if (state.app?.superAdmin?.token) {
      headers.token = state.app.superAdmin.token;
    }

    // Solo enviamos Authorization si hay un usuario logueado (normal o suplantado).
    // Si somos Super Admin puro, preferimos NO enviar Authorization manualmente 
    // para dejar que callApi lo maneje o que el backend use únicamente 'token'.
    if (state.app?.user?.token) {
      headers.Authorization = state.app.user.token;
    }

    return headers;
  };

  const get = (id, options = {}, extraOptions = {}) => {
    return async (dispatch, getState) => {
      const state = getState();
      dispatch({
        type: `FETCH_${entity.toUpperCase()}S`,
      })
      const res = await callApi(`/${entity}/${id}`, {
        method: 'GET',
        headers: getHeaders(state),
        params: options.params || {},
        ...extraOptions
      })

      dispatch({
        type: `LOAD_${entity.toUpperCase()}`,
        response: res.body,
        payload: id
      })

      return res.body;
    }
  }

  const load = (options = {}) => {
    const { page = 1, limit = 100, force = false, silent = false,
            filters = {}, orderBy = 'id', orderDir = 'ASC' } = options

    return async (dispatch, getState) => {
      const state = getState();
      const slice = state[`${entity}s`] || {};
      const cacheKey = `${entity}_page_${page}`;

      if (!force && slice._cache && slice._cache[cacheKey]) {
        const { cachedAt } = slice._cache[cacheKey];
        if (Date.now() - cachedAt < 5 * 60 * 1000) {
          return;
        }
      }

      if (!silent) {
        dispatch({ type: `FETCH_${entity.toUpperCase()}S` });
      }

      try {
        const params = { ...options.params };

        Object.keys(params).forEach(k => {
            if (k.startsWith('filter_')) return
            if (params[k] === undefined || params[k] === null || params[k] === '') delete params[k];
        });

        if (options.page || options.limit) {
          params.paginated = 'true';
          params.page = page;
          params.limit = limit;
          params.order_by = `${orderBy}:${orderDir}`;
        }

        console.log('[LOAD] entity:', entity, 'page:', page, 'limit:', limit, 'params:', JSON.stringify(params))

        Object.entries(filters).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > 0) params[`filter_${key}`] = values.join(',');
          else if (values) params[`filter_${key}`] = values;
        });

        const res = await callApi(`/${entity}`, {
          method: 'GET',
          headers: getHeaders(state),
          params,
          shouldSkipStatusError: options.shouldSkipStatusError || false,
          signal: options.signal,
          showDefaultAlertError: options.showDefaultAlertError,
        });

        if (res.status !== 200 && options.shouldSkipStatusError) return [];

        const payload = res.body?.data ? res.body : { data: res.body };
        dispatch({
          type: `LOAD_${entity.toUpperCase()}S`,
          payload: { ...payload, page, silent, cachedAt: Date.now() },
        });

        return res.body;
      } catch (error) {
        if (!silent) dispatch({ type: `FETCH_${entity.toUpperCase()}S_ERROR`, payload: error });
        if (options.signal?.aborted) throw error;
      }
    };
  };

  const prefetchNext = () => {
    return (dispatch, getState) => {
      const state = getState();
      const slice = state[`${entity}s`] || {};
      if (!slice._pagination) return;
      const { current_page, total_pages, per_page } = slice._pagination;
      const nextPage = current_page + 1;
      if (nextPage <= total_pages) {
        const cacheKey = `${entity}_page_${nextPage}`;
        if (slice._cache?.[cacheKey]) {
          const { cachedAt } = slice._cache[cacheKey];
          if (Date.now() - cachedAt < 5 * 60 * 1000) return;
        }
        dispatch(load({ page: nextPage, limit: per_page || 100, silent: true }));
      }
    };
  };

  const invalidate = () => ({ type: `INVALIDATE_${entity.toUpperCase()}S` });

  const add = (item, options = {}) => {
    const { showMessage = true } = options
    return async (dispatch, getState) => {
      const state = getState()
      const slice = state[`${entity}s`] || {}
      const currentPage = slice._pagination?.current_page || 1
      const perPage = slice._pagination?.per_page || 100
      const lang = state.app.lang ?? 'es'
      let messages = {
        en: { success: `${messageEntity} has been added successfully` },
        es: { success: `${t('Se agregó')} ${messageEntity} ${t('correctamente.')}` },
      }

      let res = await callApi(`/${entity}`, {
        method: 'POST',
        body: JSON.stringify(item),
        headers: getHeaders(state),
        successMessage: showMessage ? (messages[lang] ?? messages['es'])['success'] : undefined
      })

      let stored_item = res.body
      const addAction = dispatch({
        type: `ADD_${entity.toUpperCase()}`,
        response: stored_item,
      })
      dispatch(invalidate())
      dispatch(load({ page: currentPage, limit: perPage, force: true }))
      return addAction
    }
  }

  const remove = (item, options = {}) => {
    const { reload = true } = options
    return async (dispatch, getState) => {
      const state = getState();
      const slice = state[`${entity}s`] || {}
      const currentPage = slice._pagination?.current_page || 1
      const perPage = slice._pagination?.per_page || 100

      const res = await callApi(`/${entity}/${item.id}`, {
        method: 'DELETE',
        headers: getHeaders(state),
        successMessage: `${t('Se eliminó')} ${messageEntity} ${t('correctamente.')}`
      })
      dispatch({ type: `DELETE_${entity.toUpperCase()}`, response: res.body })
      if (reload) {
        dispatch(invalidate())
        dispatch(load({ page: currentPage, limit: perPage, force: true }))
      }
      return res.body
    }
  }

  const update = (item, shouldShowSuccessMessage = true) => {
    return async (dispatch, getState) => {
      const state = getState();
      let res = await callApi(`/${entity}/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify(item),
        headers: getHeaders(state),
        successMessage: shouldShowSuccessMessage && `${t('Se actualizó')} ${messageEntity} ${t('correctamente.')}`
      })

      let stored_item = res.body

      dispatch({
        type: `UPDATE_${entity.toUpperCase()}`,
        response: stored_item
      })
      dispatch(invalidate())
    }
  }

  const set = (id) => ({
    type: `SET_${entity.toUpperCase()}`,
    payload: id
  })

  const bulkSync = (data, options = {
    reload: true,
    showMessage: true
  }) => {
    const { deleteItems, updateItems, createItems, sys_company_id } = data;
    return async (dispatch, getState) => {
      const state = getState();

      let res = await callApi(`/${entity}/bulkSync`, {
        method: 'POST',
        body: JSON.stringify({
          deleteItems,
          updateItems,
          createItems,
          sys_company_id: sys_company_id || state?.app?.user?.sys_company_id || state?.app?.superAdmin?.sys_company_id
        }),
        headers: getHeaders(state),
      })

      if (options.showMessage) {
        Swal.fire({
          title: t('¡Acción Realizada!'),
          text: `${t('Se sincronizaron')} ${messagePluralEntity} ${t('correctamente.')}`,
          icon: 'success',
          confirmButtonText: t('OK'),
          showCloseButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            if (options.reload) {
              window.location.reload()
            }
          }
        })
      }
      return dispatch(load({
        ...options,
        force: true,
        params: { sys_company_id: sys_company_id || state?.app?.user?.sys_company_id || state?.app?.superAdmin?.sys_company_id }
      }))
    }
  }

  const bulkCreate = (arrayOfItems) => {
    return async (dispatch, getState) => {
      const state = getState();
      let res = await callApi(`/${entity}/bulkCreate`, {
        method: 'POST',
        body: JSON.stringify(arrayOfItems),
        headers: getHeaders(state),
        successMessage: `${t('Se sincronizaron')} ${messagePluralEntity} ${t('correctamente.')}`
      })
      return dispatch(load())
    }
  }

  const list = (options = {}) => {
    return async (dispatch, getState) => {
      const state = getState();
      const entities = state[`${entity}s`]?.entities || {};
      if (Object.keys(entities).length <= 2 || options.force === true) {
        dispatch({
          type: `FETCH_${entity.toUpperCase()}S`,
        })

        const { force, params, ...rest } = options;
        const queryParams = params || rest;

        const res = await callApi(`/${entity}/list`, {
          method: 'POST',
          headers: getHeaders(state),
          params: queryParams
        })

        dispatch({
          type: `LOAD_${entity.toUpperCase()}S`,
          response: res.body,
        })
        return res.body;
      }
    }
  }

  return {
    get, load, add, remove, update, set,
    bulkSync, bulkCreate, list,
    prefetchNext, invalidate,
  }
}
